import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../components/common/page-layout";
import {
  ApiKeyAdminSortField,
  SortDirection,
  useApiKeyAdminListQuery,
} from "../../generated/graphql";
import CreateApiKeyDialog from "./components/create-api-key-dialog";
import ImportCsvDialog from "./components/import-csv-dialog";
import useIsInternalAdmin from "./hooks/use-is-internal-admin";

const styles = {
  page: { p: 3 } satisfies SxProps<Theme>,
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 2,
  } satisfies SxProps<Theme>,
  searchRow: { mb: 2 } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    p: 4,
  } satisfies SxProps<Theme>,
};

/**
 * List page for the admin API key management tool. Provides search, sort,
 * paginated list, and entry points to create a blank key or import one from
 * a CSV. Rows link through to `ApiKeyAdminDetailPage`.
 *
 * Access is gated by the `INTERNAL_TOOLS` permission; unauthorized users see
 * a 403-style message rather than the list.
 */
const ApiKeysAdminListPage = () => {
  const navigate = useNavigate();
  const { canAccess, loading: permissionsLoading } = useIsInternalAdmin();

  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortField, setSortField] = useState<ApiKeyAdminSortField>(
    ApiKeyAdminSortField.Name,
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.Asc,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const debouncedSearch = useDebounce(searchInput, 250);

  const { data, loading, error } = useApiKeyAdminListQuery({
    variables: {
      input: {
        searchTerm:
          debouncedSearch.trim() === "" ? null : debouncedSearch.trim(),
        sortField,
        sortDirection,
        limit: pageSize,
        offset: page * pageSize,
      },
    },
    skip: !canAccess,
    fetchPolicy: "cache-and-network",
  });

  const rows = data?.apiKeyAdminList.apiKeys ?? [];
  const totalCount = data?.apiKeyAdminList.totalCount ?? 0;

  const handleSort = (field: ApiKeyAdminSortField) => {
    if (field === sortField) {
      setSortDirection(
        sortDirection === SortDirection.Asc
          ? SortDirection.Desc
          : SortDirection.Asc,
      );
    } else {
      setSortField(field);
      setSortDirection(SortDirection.Asc);
    }
  };

  if (permissionsLoading) {
    return (
      <PageLayout maxWidth={false} title="API keys">
        <Box sx={styles.centered}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!canAccess) {
    return (
      <PageLayout maxWidth={false} title="API keys">
        <Alert severity="error">
          This tool requires the INTERNAL_TOOLS permission. Ask an admin to
          grant access.
        </Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      maxWidth={false}
      title="API keys"
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setImportOpen(true);
            }}
          >
            Import CSV
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            Create key
          </Button>
        </Stack>
      }
    >
      {error !== undefined && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      <Box sx={styles.searchRow}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or apiKey (UUID)"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setPage(0);
          }}
        />
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === ApiKeyAdminSortField.Name}
                  direction={
                    sortDirection === SortDirection.Asc ? "asc" : "desc"
                  }
                  onClick={() => {
                    handleSort(ApiKeyAdminSortField.Name);
                  }}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Webhook URL</TableCell>
              <TableCell>Staging?</TableCell>
              <TableCell>Payload shapes</TableCell>
              <TableCell>ID format</TableCell>
              <TableCell>Custom mapping</TableCell>
              <TableCell>Handoff events</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === ApiKeyAdminSortField.OrgCount}
                  direction={
                    sortDirection === SortDirection.Asc ? "asc" : "desc"
                  }
                  onClick={() => {
                    handleSort(ApiKeyAdminSortField.OrgCount);
                  }}
                >
                  Orgs
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === ApiKeyAdminSortField.CreatedAt}
                  direction={
                    sortDirection === SortDirection.Asc ? "asc" : "desc"
                  }
                  onClick={() => {
                    handleSort(ApiKeyAdminSortField.CreatedAt);
                  }}
                >
                  Created
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No API keys match.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow
                key={row.uuid}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  navigate(`/admin/api-keys/${row.uuid}`);
                }}
              >
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.webhookUrl ?? "—"}</TableCell>
                <TableCell>{row.enableStaging ? "yes" : "no"}</TableCell>
                <TableCell>
                  {row.payloadShapes.length > 0
                    ? row.payloadShapes.join(", ")
                    : "—"}
                </TableCell>
                <TableCell>{row.idFormat}</TableCell>
                <TableCell>{row.customMapping ? "yes" : "no"}</TableCell>
                <TableCell>
                  {row.emitTerminalHandoffEvents ? "yes" : "no"}
                </TableCell>
                <TableCell>{row.orgCount}</TableCell>
                <TableCell>
                  {new Date(String(row.createdAt)).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(e) => {
          setPageSize(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
      <CreateApiKeyDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        onCreated={(uuid) => {
          setCreateOpen(false);
          navigate(`/admin/api-keys/${uuid}`);
        }}
      />
      <ImportCsvDialog
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
        }}
      />
    </PageLayout>
  );
};

/**
 * Debounces a string value so that query-driven effects only fire after
 * the user has paused typing for at least `delayMs` ms.
 */
function useDebounce(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = globalThis.setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      globalThis.clearTimeout(timer);
    };
  }, [value, delayMs]);
  return debounced;
}

export default ApiKeysAdminListPage;
